/**
 * useOneSignalNotificationEvents — page-side re-implementation of the two legacy
 * service-worker behaviours that OneSignal's own scoped worker displaces for
 * migrated browsers.
 *
 * Under the OneSignal-owns-push flag, notifications on a migrated browser are
 * displayed by OneSignal's worker (scope /push/onesignal/), NOT by the root
 * Workbox SW (src/service-worker.ts). Two behaviours the root SW implemented for
 * legacy VAPID pushes must therefore be re-provided through OneSignal's page-side
 * SDK events. The legacy SW handlers stay untouched — they still serve
 * un-migrated legacy subscriptions.
 *
 *   1. Chat-focus suppression. The root SW's `push` handler suppresses a
 *      `chat_message` notification when a focused tab is already viewing that
 *      conversation, and postMessages the page to refresh chat caches (the
 *      CHAT_MESSAGE_RECEIVED branch in useWebPushNotifications). Here we do the
 *      equivalent on OneSignal's `foregroundWillDisplay`: `preventDefault()` to
 *      stop the display, then invalidate the same three chat caches inline (we're
 *      already in the page, so no postMessage round-trip is needed).
 *   2. Click navigation. The root SW's `notificationclick` navigates via the
 *      shared `isSafeInAppPath` gate. Here we do the same on OneSignal's `click`,
 *      preferring the backend's canonical RELATIVE `additionalData.url` and
 *      falling back to `result.url` normalized to a relative path (its launch URL
 *      is absolute in production), then routing safe in-app paths through
 *      react-router; anything unsafe/external falls back to the shared guest-safe
 *      home (NOTIFICATION_TAP_FALLBACK) so a tap never dead-ends and never
 *      navigates off-origin.
 *
 * SDK shapes verified against react-onesignal 3.5.6
 * (node_modules/react-onesignal/dist/index.d.ts):
 *   - Notifications.addEventListener<K>(event, listener) / removeEventListener,
 *     with K one of 'click' | 'foregroundWillDisplay' | 'dismiss' | …
 *   - foregroundWillDisplay → { notification: IDisplayableOSNotification;
 *     preventDefault(): void }. preventDefault() IS present on web, so suppression
 *     is implementable (no fallback needed).
 *   - click → { notification: IOSNotification; result: { url?: string; actionId? } }.
 *   - IOSNotification.additionalData is typed as opaque `object`; the backend
 *     mirrors the legacy `{ type, url }` custom data onto it, so we narrow it to
 *     read `type` / `url`.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import OneSignal from 'react-onesignal';
import { whenOneSignalReady } from '@lib/onesignal';
import { useAuth } from '@hooks/useAuth';
import { useLatestRef } from '@hooks/useLatestRef';
import { shouldSuppressChatNotification } from '@lib/pwa/push-suppression';
import { isSafeInAppPath, NOTIFICATION_TAP_FALLBACK } from '@utils/safePath';
import { invalidateChatCaches } from '@lib/chatCacheInvalidation';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

/**
 * OneSignal types `additionalData` as opaque `object`. Narrow to the two fields
 * the backend carries (mirroring the legacy VAPID `data: { type, url }` payload).
 */
interface NotificationCustomData {
  type?: string;
  url?: string;
}

const readCustomData = (additionalData: object | undefined): NotificationCustomData =>
  (additionalData ?? {}) as NotificationCustomData;

/**
 * Normalize OneSignal's `result.url` (the notification's launch URL, ABSOLUTE in
 * production) into a candidate for the safe-path gate.
 *   - Same-origin absolute → relative `pathname + search + hash` (passes the gate).
 *   - Foreign-origin absolute → returned unchanged, so it still carries a scheme
 *     and the gate rejects it (falls back to '/').
 *   - Malformed / unparseable → undefined, so the gate rejects it.
 * Never throws: a bad launch URL must not escape the click handler.
 */
const normalizeResultUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : url;
  } catch {
    return undefined;
  }
};

/**
 * Re-provides legacy SW chat-focus suppression + safe click navigation for
 * OneSignal-displayed notifications. Void hook — mount it once under the
 * OneSignal push owner path (OneSignalPushInitializer). No-op until
 * whenOneSignalReady() resolves (it REJECTS when OneSignal is unconfigured / init
 * failed, so the effect stays inert on the legacy path).
 */
export function useOneSignalNotificationEvents(): void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;

  // Refs for navigate + the current userId so the SDK listeners (registered
  // once, after an async readiness gate) never capture a stale value.
  // useLatestRef syncs via effect — never assigned during render — to satisfy
  // Rules of React / the compiler.
  const navigateRef = useLatestRef(navigate);
  const userIdRef = useLatestRef(userId);

  // Register OneSignal notification-event listeners once ready. This is external-
  // system synchronisation (the OneSignal SDK), the one sanctioned useEffect use.
  useEffect(() => {
    let cancelled = false;

    const handleForegroundWillDisplay = (event: {
      notification: { additionalData?: object };
      preventDefault: () => void;
    }) => {
      const data = readCustomData(event.notification.additionalData);
      // Page-context equivalent of the SW's focused-client scan: this tab counts
      // as a focused client only when the document actually has focus.
      const focusedClientUrls = document.hasFocus() ? [window.location.href] : [];
      const { suppress } = shouldSuppressChatNotification(focusedClientUrls, data);
      if (!suppress) return;

      // Stop OneSignal displaying the banner for a conversation already on screen…
      event.preventDefault();
      // …and refresh the same three chat caches the legacy CHAT_MESSAGE_RECEIVED
      // branch does (via the shared helper, so both push paths stay in lockstep)
      // so the suppressed message appears inline. userId via ref to avoid a stale
      // capture.
      void invalidateChatCaches(queryClient, userIdRef.current);
    };

    const handleClick = (event: {
      notification: { additionalData?: object };
      result: { url?: string };
    }) => {
      const data = readCustomData(event.notification.additionalData);
      // Resolve the tap target, then gate it through the shared safe-path check
      // (same gate as the SW / native tap): anything unsafe or external falls back
      // to the shared guest-safe home (NOTIFICATION_TAP_FALLBACK) so a tap never
      // dead-ends and never navigates off-origin.
      //
      // Precedence: the backend's canonical RELATIVE deep link (data.url) FIRST —
      // same rationale as PR #820's native fix. OneSignal's result.url is the
      // launch URL and is ABSOLUTE in production (https://app.meetball.fun/chat/…);
      // isSafeInAppPath rejects anything containing '://', so consulting it raw
      // would drop every deep-linked tap onto the generic fallback. Only when
      // data.url is absent do we fall back to result.url, normalized to a relative
      // path when it points at our own origin (mirrors the suppression handler's
      // new URL(...) reuse).
      const candidate = data.url ?? normalizeResultUrl(event.result.url);
      navigateRef.current(isSafeInAppPath(candidate) ? candidate : NOTIFICATION_TAP_FALLBACK);
    };

    whenOneSignalReady()
      .then(() => {
        if (cancelled) return;
        OneSignal.Notifications.addEventListener(
          'foregroundWillDisplay',
          handleForegroundWillDisplay,
        );
        OneSignal.Notifications.addEventListener('click', handleClick);
      })
      .catch((error: unknown) => {
        // Readiness rejects when OneSignal is unconfigured / init failed (e.g. the
        // legacy path, where this hook is never meant to act). Nothing to wire up.
        logger.error(
          'OneSignal push: readiness failed while wiring notification events',
          sanitizeError(error),
        );
      });

    return () => {
      cancelled = true;
      // Safe even if the listeners were never added (readiness rejected / still
      // pending): removeEventListener on an unknown handler is a no-op.
      OneSignal.Notifications.removeEventListener(
        'foregroundWillDisplay',
        handleForegroundWillDisplay,
      );
      OneSignal.Notifications.removeEventListener('click', handleClick);
    };
    // navigateRef/userIdRef are stable useLatestRef handles - listed to
    // satisfy exhaustive-deps; the SDK listeners still register once per ready.
  }, [queryClient, navigateRef, userIdRef]);
}
